import { agentPrompt } from './agent-prompts.js';
import { type AgentDefinition, HEAVY_BUDGET, MEDIUM_BUDGET, TOOLS } from './types.js';

/** Phase 4 · Verify — prove the code works under normal, end-to-end, and adverse conditions. */
export const VERIFY_AGENTS: AgentDefinition[] = [
  {
    config: {
      id: 'verifier',
      name: 'Verifier',
      role: 'verifier',
      tools: [...TOOLS.inspect, 'bash', 'exec', 'lint', 'typecheck', 'test', 'git'],
      prompt: agentPrompt('verifier'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Independent verification gate: runs the relevant checks until they pass or returns exact failures that need another implementation pass.',
      keywords: [
        'verify',
        'verification',
        'quality gate',
        'prove it works',
        'run checks',
        'run tests until pass',
        'test pass',
        'green build',
        'typecheck',
        'lint',
        'regression check',
        'acceptance gate',
      ],
    },
  },
  {
    config: {
      id: 'test',
      name: 'Test',
      role: 'test',
      tools: [...TOOLS.build],
      prompt: agentPrompt('test'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Unit + integration testing: writes meaningful tests covering golden path and edge cases, runs the suite.',
      keywords: [
        'test',
        'unit test',
        'integration test',
        'write tests',
        'coverage',
        'test suite',
        'vitest',
        'jest',
        'add tests',
        'spec',
      ],
    },
  },
  {
    config: {
      id: 'e2e',
      name: 'E2E',
      role: 'e2e',
      tools: [
        ...TOOLS.build,
        'fetch',
        'playwright_navigate',
        'playwright_screenshot',
        'playwright_click',
        'playwright_type',
        'playwright_evaluate',
        'playwright_select_option',
        'playwright_hover',
        'playwright_fill_form',
        'playwright_wait_for',
        'playwright_press_key',
        'playwright_drag',
      ],
      prompt: agentPrompt('e2e'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'End-to-end testing: drives full user journeys across UI/CLI/API boundaries with reproducible failures.',
      keywords: [
        'e2e',
        'end to end',
        'end-to-end',
        'user journey',
        'smoke test',
        'playwright',
        'browser',
        'screenshot',
        'web ui',
        'headless',
        'cypress',
        'full flow',
        'browser test',
        'acceptance test',
        'navigate',
        'click',
        'form fill',
        'dom',
        'page load',
      ],
    },
  },
  {
    config: {
      id: 'browser',
      name: 'Browser',
      role: 'browser',
      tools: [
        ...TOOLS.read,
        'fetch',
        'playwright_navigate',
        'playwright_screenshot',
        'playwright_click',
        'playwright_type',
        'playwright_evaluate',
        'playwright_select_option',
        'playwright_hover',
        'playwright_fill_form',
        'playwright_wait_for',
        'playwright_press_key',
        'playwright_drag',
      ],
      prompt: agentPrompt('browser'),
    },
    budget: MEDIUM_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Browser automation: opens pages, clicks, types, screenshots, extracts data via Playwright headless Chromium.',
      keywords: [
        'browser',
        'screenshot',
        'navigate',
        'web page',
        'scrape',
        'crawl',
        'headless',
        'chrome',
        'open url',
        'capture',
        'page title',
        'extract data',
        'fill form',
        'click button',
        'take screenshot',
      ],
    },
  },
  {
    config: {
      id: 'performance',
      name: 'Performance',
      role: 'performance',
      tools: [...TOOLS.build, 'logs'],
      prompt: agentPrompt('performance'),
    },
    budget: MEDIUM_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Performance analysis: benchmarks/profiles to find the real bottleneck, optimizes, proves speedup with numbers.',
      keywords: [
        'performance',
        'slow',
        'optimize',
        'bottleneck',
        'profile',
        'benchmark',
        'latency',
        'throughput',
        'memory',
        'speed up',
        'too slow',
      ],
    },
  },
  {
    config: {
      id: 'chaos',
      name: 'Chaos',
      role: 'chaos',
      tools: [...TOOLS.build, 'logs'],
      prompt: agentPrompt('chaos'),
    },
    budget: MEDIUM_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Resilience testing via fault injection: breaks network/disk/timing to find ungraceful failures and recovery gaps.',
      keywords: [
        'chaos',
        'resilience',
        'fault injection',
        'failure mode',
        'fail safe',
        'retry',
        'circuit breaker',
        'graceful degradation',
        'inject failure',
        'robustness',
      ],
    },
  },
  {
    config: {
      id: 'security-scanner',
      name: 'Security Scanner',
      role: 'security-scanner',
      tools: [...TOOLS.inspect],
      prompt: agentPrompt('security-scanner'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Security scanner: detects hardcoded secrets, injection vectors, insecure patterns, and supply-chain risks with remediation.',
      keywords: [
        'security',
        'scan',
        'vulnerability',
        'secret',
        'api key',
        'hardcoded',
        'injection',
        'cve',
        'audit dependencies',
        'supply chain',
        'xss',
        'sqli',
        'shell injection',
        'sensitive data',
        'credential',
      ],
    },
  },
  {
    config: {
      id: 'bug-hunter',
      name: 'Bug Hunter',
      role: 'bug-hunter',
      tools: [...TOOLS.inspect],
      prompt: agentPrompt('bug-hunter'),
    },
    budget: HEAVY_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Bug hunter: scans source code for bugs, anti-patterns, and code smells, producing a file:line-ranked hit list with fixes.',
      keywords: [
        'bug',
        'hunt',
        'scan',
        'code smell',
        'anti-pattern',
        'race condition',
        'memory leak',
        'null deref',
        'type safety',
        'unhandled error',
        'find bugs',
        'audit code',
        'code quality',
      ],
    },
  },
  {
    config: {
      id: 'audit-log',
      name: 'Audit Log',
      role: 'audit-log',
      tools: [...TOOLS.inspect],
      prompt: agentPrompt('audit-log'),
    },
    budget: MEDIUM_BUDGET,
    capability: {
      phase: 'verify',
      summary:
        'Audit log analyzer: parses session JSONL, detects failure patterns, tool anomalies, and cost trends with structured reports.',
      keywords: [
        'audit',
        'log',
        'logs',
        'session',
        'trace',
        'analyze logs',
        'error patterns',
        'cost analysis',
        'tool usage',
        'token usage',
        'post-mortem',
        'trend',
        'anomaly',
      ],
    },
  },
];
